import { computed, unref } from '@vue/composition-api'
import horsesApi from '@/services/api/horses'
import useServerList from '@/composables/useServerList'

function mapRow(h) {
  return {
    id: h.id,
    name: h.name,
    dob: h.dob,
    horseStatusId: h.horse_status_id,
    boyStatusId: h.boy_status_id,
    registrationDate: h.registration_date,
    active: h.active,
    beginningOfYearStatus: h.beginning_of_year_status,
    ownerId: h.owner_id,
    ownerName: h.owner_name,
    ownerDisplayName: h.owner_display_name,
    riders: h.riders,
    zoneId: h.zone_id,
    // Only present (true/false) when the request carried member_id — the Member Profile
    // "Horses" tab (CAM-1505). Undefined everywhere else. Both false = the member sees the
    // horse only because a family member owns/rides it (can be both true at once).
    isOwner: h.is_owner,
    isRider: h.is_rider
  }
}

/**
 * Horses list — /horses, /secretary-horses, /admin-horses and the Member Profile "Horses" tab
 * all use this. Server decides the scope from the token (+ `memberId` below); here we only
 * carry the shared table state (page / sort / per-page / search / registration tab), URL-synced
 * via useServerList.
 *
 * @param {boolean|import('@vue/composition-api').Ref<boolean>} own  the "My Horses" page passes
 *   this (a ref, so it tracks route changes when this component is reused) so a member-Secretary
 *   sees only their own horses there — they still get the whole register on /secretary-horses.
 *   Not user-facing, fixed per page.
 * @param {number|import('@vue/composition-api').Ref<number>|null} memberId  the Member Profile
 *   "Horses" tab passes the profile's member id so a secretary/admin sees THAT member's family
 *   instead of the whole register. Mutually exclusive with `own`. Backend rejects this param for
 *   anyone below secretary (HorseIndexRequest::authorize()).
 */
export default function useHorsesList(own = false, memberId = null) {
  const getList = (params) => {
    if (unref(own)) return horsesApi.getList({ ...params, own: 1 })
    const mid = unref(memberId)
    return horsesApi.getList(mid ? { ...params, member_id: mid } : params)
  }

  const list = useServerList(getList, {
    defaultSort: 'name',
    defaultDirection: 'asc',
    defaultPerPage: 20,
    // Array defaults => multi-value filters: CSV in the URL / request, number[] in state
    // (string[] for `relation`, see stringMultiFilters below).
    extraFilters: {
      registration_state: 'all',
      active: '', // '' = all, '1' = active, '0' = archived
      zone_id: [],
      status_id: [],
      boy_status_id: [],
      // 'owner' | 'rider' | 'family' — only meaningful together with `memberId` (Member Profile
      // "Horses" tab); a no-op filter for the other three pages, which never render its control.
      relation: []
    },
    stringMultiFilters: ['relation']
  })

  const horses = computed(() => list.rows.value.map(mapRow))

  return { ...list, horses }
}



// WEBPACK FOOTER //
// ./src/composables/horses/useHorsesList.js